import { Link } from "@tanstack/react-router";
import { Facebook, Menu, X } from "lucide-react";
import { lazy, Suspense, useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { Button } from "@/components/ui/button";
import { SITE } from "@/config/site";
import { getSiteLogo } from "@/lib/api/public-data.functions";
import defaultLogo from "@/assets/site-logo-default.webp";

const DEFAULT_LOGO_SRC = defaultLogo;

const ProfileMenu = lazy(() =>
  import("@/components/ProfileMenu").then((m) => ({ default: m.ProfileMenu })),
);

const NAV = [
  { to: "/", label: "Home" },
  { to: "/events", label: "Events" },
  { to: "/gallery", label: "Gallery" },
  { to: "/blog", label: "Blog" },
  { to: "/enquiry", label: "Enquiry" },
  { to: "/book", label: "Book Now" },
];

export function SiteHeader() {
  const [open, setOpen] = useState(false);
  const [authed, setAuthed] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const getLogo = useServerFn(getSiteLogo);
  const { data: logoUrl } = useQuery({
    queryKey: ["siteLogo"],
    queryFn: () => getLogo(),
    staleTime: 5 * 60_000,
  });
  const logoSrc = logoUrl || DEFAULT_LOGO_SRC;

  useEffect(() => {
    let unsub: (() => void) | undefined;
    const init = async () => {
      const { supabase } = await import("@/integrations/supabase/client");
      const check = async (session: any) => {
        setAuthed(!!session);
        if (session?.user) {
          const { data } = await supabase.rpc("has_role", { _user_id: session.user.id, _role: "admin" });
          setIsAdmin(!!data);
        } else setIsAdmin(false);
      };
      const { data } = await supabase.auth.getSession();
      check(data.session);
      const { data: { subscription } } = supabase.auth.onAuthStateChange((_e, s) => check(s));
      unsub = () => subscription.unsubscribe();
    };
    const ric = (window as any).requestIdleCallback as
      | ((cb: () => void, opts?: { timeout: number }) => number)
      | undefined;
    if (ric) ric(() => void init(), { timeout: 2000 });
    else setTimeout(() => void init(), 200);
    return () => { unsub?.(); };
  }, []);

  return (
    <header className="sticky top-0 z-50 border-b border-border/60 bg-background/85 backdrop-blur-md">
      <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-3 sm:px-6">
        <Link to="/" className="flex items-center gap-3 leading-tight">
          <img
            src={logoSrc}
            alt="Sanchayeeta — Versatile Singer"
            width={48}
            height={48}
            className="h-10 w-10 rounded-full object-cover ring-2 ring-gold/60 sm:h-12 sm:w-12"
          />
          <span className="flex flex-col">
            <span className="font-script text-2xl text-primary sm:text-3xl">Surer Adda</span>
            <span className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground sm:text-xs">
              with Sanchayeeta
            </span>
          </span>
        </Link>

        <nav className="hidden items-center gap-7 md:flex">
          {NAV.map((n) => (
            <Link
              key={n.to}
              to={n.to}
              className="text-sm font-medium text-foreground/80 transition hover:text-primary"
              activeProps={{ className: "text-primary" }}
            >
              {n.label}
            </Link>
          ))}
          <a
            href={SITE.facebookUrl}
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Facebook page"
            className="text-foreground/70 transition hover:text-primary"
          >
            <Facebook className="h-5 w-5" />
          </a>
          {authed ? (
            <>
              {isAdmin && (
                <Link to="/admin"><Button variant="outline" size="sm">Admin</Button></Link>
              )}
              <Link to="/ai-studio">
                <Button variant="outline" size="sm">AI Studio</Button>
              </Link>
              <Link to="/my-bookings">
                <Button variant="outline" size="sm">My Bookings</Button>
              </Link>
              <Suspense fallback={null}><ProfileMenu onSignedOut={() => setOpen(false)} /></Suspense>
            </>
          ) : (
            <Link to="/auth">
              <Button size="sm" className="bg-primary text-primary-foreground">Sign in</Button>
            </Link>
          )}
        </nav>

        <button
          aria-label="Toggle menu"
          className="md:hidden"
          onClick={() => setOpen((v) => !v)}
        >
          {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>

      {open && (
        <div className="border-t border-border/60 bg-background md:hidden">
          <div className="flex flex-col px-4 py-3">
            {NAV.map((n) => (
              <Link
                key={n.to}
                to={n.to}
                className="py-2 text-sm"
                onClick={() => setOpen(false)}
              >
                {n.label}
              </Link>
            ))}
            {authed && isAdmin && (
              <Link to="/admin" onClick={() => setOpen(false)} className="py-2 text-sm font-medium text-primary">Admin Panel</Link>
            )}
            {authed && (
              <Link to="/ai-studio" onClick={() => setOpen(false)} className="py-2 text-sm font-medium text-primary">AI Studio</Link>
            )}
            <Link to={authed ? "/my-bookings" : "/auth"} onClick={() => setOpen(false)} className="py-2 text-sm font-medium text-primary">
              {authed ? "My Bookings" : "Sign in / Register"}
            </Link>
            {authed && (
              <div className="py-2"><Suspense fallback={null}><ProfileMenu compact onSignedOut={() => setOpen(false)} /></Suspense></div>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
